import React, { useState, useEffect } from 'react'
import { useData } from '../contexts/DataContext'

const Admin = () => {
  const { 
    members, 
    fetchMembers, 
    createMember, 
    deleteMember, 
    settings, 
    fetchSettings, 
    updateSettings,
    fetchMembersWithPasswords,
    fetchMemberDetails
  } = useData()
  
  const [memberName, setMemberName] = useState('')
  const [loading, setLoading] = useState(false)
  const [showPasswords, setShowPasswords] = useState(false)
  const [membersWithPasswords, setMembersWithPasswords] = useState([])
  const [selectedMemberDetails, setSelectedMemberDetails] = useState(null)
  const [showMemberModal, setShowMemberModal] = useState(false)

  useEffect(() => {
    fetchMembers()
    fetchSettings()
  }, [])

  // Load members with passwords when toggle is enabled
  useEffect(() => {
    if (showPasswords) {
      const loadMembersWithPasswords = async () => {
        const data = await fetchMembersWithPasswords()
        setMembersWithPasswords(data)
      }
      loadMembersWithPasswords()
    }
  }, [showPasswords])

  const handleAddMember = async () => {
    const name = memberName.trim()
    if (!name) return

    setLoading(true)
    const result = await createMember({ name, password: 'demo' })
    if (result.success) {
      setMemberName('')
    } else {
      alert(result.error)
    }
    setLoading(false)
  }

  const handleDeleteMember = async (memberId) => {
    if (!confirm('Are you sure you want to remove this member?')) return

    setLoading(true)
    const result = await deleteMember(memberId)
    if (!result.success) {
      alert(result.error)
    }
    setLoading(false)
  }

  const handleUpdateKioskShowList = async (showList) => {
    const result = await updateSettings({ kioskShowList: showList })
    if (!result.success) {
      alert(result.error)
    }
  }

  const handleUpdateTheme = async (themeUpdates) => {
    const result = await updateSettings({ 
      theme: { ...settings.theme, ...themeUpdates } 
    })
    if (!result.success) {
      alert(result.error)
    }
  }

  const handleMemberClick = async (memberId) => {
    setLoading(true)
    const result = await fetchMemberDetails(memberId)
    if (result.success) {
      setSelectedMemberDetails(result.data)
      setShowMemberModal(true)
    } else {
      alert(result.error)
    }
    setLoading(false)
  }

  const displayMembers = showPasswords ? membersWithPasswords : members

  return (
    <div className="admin-dashboard">
      {/* Dashboard Header */}
      <div className="dashboard-header">
        <div className="header-content">
          <h1>Admin Dashboard</h1>
          <p className="header-subtitle">Manage your gym members, settings, and monitor activity</p>
        </div>
        <div className="header-stats">
          <div className="stat-card">
            <div className="stat-number">{members.length}</div>
            <div className="stat-label">Total Members</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">{members.filter(m => m.isActive !== false).length}</div>
            <div className="stat-label">Active Members</div>
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="dashboard-grid">
        
        {/* Members Management Section */}
        <div className="admin-section">
          <div className="section-header">
            <div className="section-title">
              <h2>👥 Member Management</h2>
              <p>Add, remove, and manage gym members</p>
            </div>
          </div>
          
          <div className="section-content">
            {/* Add Member Form */}
            <div className="add-member-form">
              <div className="form-row">
                <input 
                  className="form-input flex-1"
                  placeholder="Enter member's full name" 
                  value={memberName} 
                  onChange={e => setMemberName(e.target.value)}
                  disabled={loading}
                />
                <button 
                  className="btn primary"
                  onClick={handleAddMember}
                  disabled={loading || !memberName.trim()}
                >
                  {loading ? <span className="spinner"></span> : '+'} Add Member
                </button>
              </div>
            </div>

            {/* Password Toggle */}
            <div className="toggle-section">
              <label className="toggle-label">
                <input 
                  type="checkbox" 
                  checked={showPasswords} 
                  onChange={e => setShowPasswords(e.target.checked)}
                  className="toggle-input"
                />
                <span className="toggle-slider"></span>
                Show member passwords
              </label>
            </div>
            
            {/* Members List */}
            <div className="members-list">
              {displayMembers.map(member => (
                <div key={member._id} className="member-card">
                  <div className="member-info">
                    <div className="member-name" onClick={() => handleMemberClick(member._id)}>
                      {member.name}
                    </div>
                    {showPasswords && (
                      <div className="member-password">
                        <span className="password-label">Password:</span>
                        <code className="password-value">{member.decryptedPassword || 'Unable to decrypt'}</code>
                      </div>
                    )}
                    <div className="member-meta">
                      Member since {new Date(member.createdAt).toLocaleDateString()}
                    </div>
                  </div>
                  <button 
                    className="btn danger sm"
                    onClick={() => handleDeleteMember(member._id)}
                    disabled={loading}
                    title="Remove member"
                  >
                    🗑️
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Settings Section */}
        <div className="admin-section">
          <div className="section-header">
            <div className="section-title">
              <h2>⚙️ System Settings</h2>
              <p>Configure kiosk and application settings</p>
            </div>
          </div>
          
          <div className="section-content">
            
            {/* Kiosk Settings */}
            <div className="settings-group">
              <h3>Kiosk Configuration</h3>
              <div className="setting-item">
                <label className="toggle-label">
                  <input 
                    type="checkbox" 
                    checked={settings?.kioskShowList || false} 
                    onChange={e => handleUpdateKioskShowList(e.target.checked)}
                    className="toggle-input"
                  />
                  <span className="toggle-slider"></span>
                  Show exercise list on kiosk left panel
                </label>
              </div>
            </div>

            {/* Admin Login Info */}
            <div className="settings-group">
              <h3>Admin Authentication</h3>
              <div className="admin-password-info">
                <div className="info-box">
                  <span className="info-icon">🔑</span>
                  <div>
                    <div className="info-title">Admin Password</div>
                    <div className="info-text">Current: <code>bfit123</code></div>
                    <div className="info-note">Change in environment variables</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Theme Settings */}
            {settings && (
              <div className="settings-group">
                <h3>Theme Customization</h3>
                <div className="theme-controls">
                  <div className="color-control">
                    <label>Accent Color</label>
                    <div className="color-input-wrapper">
                      <input 
                        type="color" 
                        value={settings.theme?.accent || '#e87722'} 
                        onChange={e => handleUpdateTheme({ accent: e.target.value })}
                        className="color-input"
                      />
                      <span className="color-value">{settings.theme?.accent || '#e87722'}</span>
                    </div>
                  </div>
                  
                  <div className="color-control">
                    <label>Top Bar Color</label>
                    <div className="color-input-wrapper">
                      <input 
                        type="color" 
                        value={settings.theme?.topbarBg || '#212322'} 
                        onChange={e => handleUpdateTheme({ topbarBg: e.target.value })}
                        className="color-input"
                      />
                      <span className="color-value">{settings.theme?.topbarBg || '#212322'}</span>
                    </div>
                  </div>
                  
                  <div className="range-control">
                    <label>Kiosk Background Opacity</label>
                    <div className="range-wrapper">
                      <input 
                        type="range" 
                        min="0" 
                        max="0.8" 
                        step="0.02" 
                        value={settings.theme?.kioskBgOpacity || 0.24} 
                        onChange={e => handleUpdateTheme({ kioskBgOpacity: Number(e.target.value) })}
                        className="range-input"
                      />
                      <span className="range-value">{Math.round((settings.theme?.kioskBgOpacity || 0.24) * 100)}%</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Background Settings */}
            <div className="settings-group">
              <h3>Background Images</h3>
              <div className="background-info">
                <div className="current-bg">
                  <span>Active Background:</span>
                  <span className="bg-name">{settings?.kioskBgs?.items?.find(i => i.id === settings.kioskBgs?.activeId)?.name || 'Default'}</span>
                </div>
                <button 
                  className="btn secondary"
                  onClick={() => alert('Background management feature coming soon')}
                >
                  Manage Backgrounds
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions & Tips */}
        <div className="admin-section tips-section">
          <div className="section-header">
            <div className="section-title">
              <h2>💡 Quick Guide</h2>
              <p>Tips and shortcuts for managing your gym</p>
            </div>
          </div>
          
          <div className="section-content">
            <div className="tips-grid">
              <div className="tip-card">
                <div className="tip-icon">🏗️</div>
                <div className="tip-content">
                  <h4>Exercise Builder</h4>
                  <p>Use the <strong>BUILDER</strong> tab to create custom exercises, workouts, and subcategories</p>
                </div>
              </div>
              
              <div className="tip-card">
                <div className="tip-icon">📊</div>
                <div className="tip-content">
                  <h4>Data Export</h4>
                  <p>Use <strong>EXPORT</strong> to download member data and workout history as CSV files</p>
                </div>
              </div>
              
              <div className="tip-card">
                <div className="tip-icon">📱</div>
                <div className="tip-content">
                  <h4>Kiosk Mode</h4>
                  <p>Click <strong>KIOSK</strong> to test the member interface on tablets at workout stations</p>
                </div>
              </div>
              
              <div className="tip-card">
                <div className="tip-icon">👤</div>
                <div className="tip-content">
                  <h4>Member Details</h4>
                  <p>Click any member name to view their workout history, stats, and account details</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced Member Details Modal */}
      {showMemberModal && selectedMemberDetails && (
        <div className="modal-overlay" onClick={() => setShowMemberModal(false)}>
          <div className="modal member-details-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="member-header-info">
                <h2>{selectedMemberDetails.member.name}</h2>
                <span className="member-badge">Member Profile</span>
              </div>
              <button 
                className="btn ghost sm" 
                onClick={() => setShowMemberModal(false)}
                aria-label="Close modal"
              >
                ✕
              </button>
            </div>
            
            <div className="modal-content">
              {/* Member Overview Cards */}
              <div className="member-overview">
                <div className="overview-card">
                  <div className="overview-icon">👤</div>
                  <div className="overview-content">
                    <div className="overview-title">Account Info</div>
                    <div className="overview-details">
                      <div><strong>Password:</strong> <code>{selectedMemberDetails.member.password}</code></div>
                      <div><strong>Role:</strong> {selectedMemberDetails.member.role}</div>
                      <div><strong>Member since:</strong> {new Date(selectedMemberDetails.member.createdAt).toLocaleDateString()}</div>
                      {selectedMemberDetails.member.lastLogin && (
                        <div><strong>Last login:</strong> {new Date(selectedMemberDetails.member.lastLogin).toLocaleDateString()}</div>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="overview-card">
                  <div className="overview-icon">📊</div>
                  <div className="overview-content">
                    <div className="overview-title">Workout Stats</div>
                    <div className="overview-details">
                      <div><strong>Total workouts:</strong> {selectedMemberDetails.stats.totalWorkouts}</div>
                      {selectedMemberDetails.stats.lastWorkout ? (
                        <div><strong>Last workout:</strong> {new Date(selectedMemberDetails.stats.lastWorkout).toLocaleDateString()}</div>
                      ) : (
                        <div><strong>Last workout:</strong> <span className="muted">Never</span></div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Recent Workouts */}
              <div className="workout-history">
                <h3>Recent Workouts</h3>
                <div className="workout-list">
                  {selectedMemberDetails.recentResults.length > 0 ? (
                    selectedMemberDetails.recentResults.map((result, index) => (
                      <div key={result._id} className="workout-item">
                        <div className="workout-header">
                          <div className="workout-exercise">
                            {result.exerciseId?.name || 'Unknown Exercise'}
                            {result.subId && <span className="workout-sub"> → {result.subId}</span>}
                          </div>
                          <div className="workout-date">
                            {new Date(result.createdAt).toLocaleDateString()}
                          </div>
                        </div>
                        <div className="workout-details">
                          {result.payload.weight && <span className="detail-item">💪 {result.payload.weight}lbs</span>}
                          {result.payload.reps && <span className="detail-item">🔄 {result.payload.reps} reps</span>}
                          {result.payload.sets && <span className="detail-item">📊 {result.payload.sets} sets</span>}
                          {result.payload.time && <span className="detail-item">⏱️ {result.payload.time}</span>}
                          {result.payload.value && <span className="detail-item">📈 {result.payload.value} {result.payload.unit || ''}</span>}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="no-workouts">
                      <div className="no-workouts-icon">🏋️‍♂️</div>
                      <div className="no-workouts-text">No workouts recorded yet</div>
                      <div className="no-workouts-sub">This member hasn't logged any exercises</div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Admin
